import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { Icon } from "../Icon"
import { ComponentError } from "../util/components"
import { useClickOutside } from "../util/hooks"
import {
  ArrowIcon,
  Clear,
  Curtain,
  CurtainOption,
  SearchWrapper,
  SelectDisplay,
  Wrapper,
} from "./components"
import type { SelectOption, SelectProps, SelectRef } from "./types"

const BaseSelect = <ValueType extends string | number, ExtraType = any>(
  {
    className,
    id,
    role,

    value,
    options,

    error,
    errorTimeout,
    hideErrorMessage = false,

    placeholder = "Select from list...",
    noOptionsText = "No more options",
    searchable = false,
    nullable = false,
    tabIndex = 0,
    disabled = false,

    inline = false,
    width = "332px",
    fullWidth = false,
    minWidth,
    maxWidth,
    height = "32px",

    color = "text.dark",
    background = "white",

    iconProps,
    clickOutsideExclude = [],

    onErrorTimeout = () => void 0,
    onClick = () => void 0,
    onKeyDown = () => void 0,
    onOpen = () => true,
    onClose = () => true,
    onFilterChange = () => void 0,
    onCurtainScroll,
    onChange,
  }: SelectProps<ValueType, ExtraType>,
  ref?: React.ForwardedRef<SelectRef>,
) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const curtainRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [filter, setFilter] = useState("")
  const [internalError, setInternalError] = useState(error)

  useImperativeHandle(ref, () => ({
    toggleOpen(open) {
      setOpen((v) => (typeof open === "undefined" ? !v : open))
    },
  }))

  useEffect(() => {
    setInternalError(error)
  }, [error])

  const updateFilter = useCallback(
    (filter: string) => {
      setFilter(filter)
      onFilterChange(filter)
    },
    [onFilterChange],
  )

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1)
      updateFilter("")
    }
  }, [open, updateFilter])

  useEffect(() => {
    if (disabled && open) setOpen(false)
  }, [disabled, open])

  useEffect(() => {
    const el = curtainRef.current
    if (!el) return

    try {
      const activeOption = el.querySelectorAll(".--select-curtain-option")[
        activeIndex
      ]

      if (!activeOption) return
      activeOption.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    } catch {}
  }, [activeIndex])

  useClickOutside(
    wrapperRef,
    () => toggleOpen(false),
    true,
    ...clickOutsideExclude,
  )

  const filteredOptions = useMemo(
    () =>
      !searchable || filter === ""
        ? options
        : options.filter(
            typeof searchable === "boolean"
              ? (option) =>
                  typeof option.label == "string"
                    ? option.label
                        .toLocaleLowerCase()
                        .includes(filter.toLocaleLowerCase())
                    : true
              : searchable === "custom"
                ? () => true
                : (option) => searchable(option, filter),
          ),
    [options, filter, searchable],
  )

  function toggleOpen(doOpen: boolean) {
    if (disabled) return
    const proceed = doOpen ? onOpen() : onClose()
    if (proceed !== false) setOpen(doOpen)
  }

  function cycleActive(delta: number) {
    const clamp = (n: number) =>
      Math.max(-1, Math.min(n, filteredOptions.length))
    setActiveIndex((idx) => clamp(idx + delta))
  }
  function cycleSelected(delta: number) {
    const idx = filteredOptions.findIndex((opt) => opt.value === value)
    if (idx < 0) return

    const newIdx = Math.max(
      0,
      Math.min(idx + delta, filteredOptions.length - 1),
    )
    const newOption = filteredOptions[newIdx]

    onChange(newOption.value, newOption)
  }

  function handleSelect(option: SelectOption<ValueType, ExtraType> | null) {
    onChange(option?.value ?? null, option)
    toggleOpen(false)
  }

  function handleKeyDown(evt: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown(evt)
    if (evt.defaultPrevented) return

    switch (evt.key) {
      case "ArrowDown":
        open ? cycleActive(1) : cycleSelected(1)
        break
      case "ArrowUp":
        open ? cycleActive(-1) : cycleSelected(-1)
        break
      case "Enter":
        if (!open) toggleOpen(true)
        else if (activeIndex > -1 && activeIndex < filteredOptions.length) {
          handleSelect(filteredOptions[activeIndex])
          setActiveIndex((i) => Math.max(i - 1, 0))
        }
        break
    }
  }

  function handleClearClick(evt: React.MouseEvent) {
    evt.stopPropagation()
    handleSelect(null)
  }

  function handleErrorTimeout() {
    setInternalError(null)
    onErrorTimeout()
  }

  const selectedOption = useMemo(
    () => filteredOptions.find((o) => o.value === value) ?? null,
    [filteredOptions, value],
  )

  return (
    <Wrapper
      className={className}
      id={id}
      role={role}
      ref={wrapperRef}
      disabled={disabled}
      width={fullWidth ? "100%" : width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      inline={inline}
      onKeyDown={handleKeyDown}
      onClick={onClick}
    >
      <ComponentError
        className="--select-error"
        error={internalError}
        hide={hideErrorMessage}
        timeout={errorTimeout}
        onTimeout={handleErrorTimeout}
      />

      <SelectDisplay
        className={
          "--select-display" + (disabled ? " --select-display-disabled" : "")
        }
        open={open}
        tabIndex={tabIndex}
        disabled={disabled}
        height={height}
        color={color}
        background={background}
        onClick={() => toggleOpen(!open)}
      >
        <div className="--select-display-inner">
          {selectedOption !== null ? (
            <span className="--select-display-selected">
              {selectedOption.label}
              {nullable && (
                <Clear
                  className="--select-display-selected-clear"
                  onClick={handleClearClick}
                >
                  <Icon icon="times" />
                </Clear>
              )}
            </span>
          ) : (
            <span className="--select-placeholder">{placeholder}</span>
          )}
        </div>
        <ArrowIcon
          className="--select-arrow-icon"
          icon={open ? "chevron-up" : "chevron-down"}
          type="light"
          color="gray6"
          {...iconProps}
        />
      </SelectDisplay>

      {open && (
        <Curtain
          ref={curtainRef}
          className="--select-curtain"
          color={color}
          background={background}
          onScroll={onCurtainScroll}
        >
          <div className="--select-curtain-inner">
            {!!searchable && (
              <SearchWrapper className="--select-curtain-search">
                <input
                  value={filter}
                  onChange={(e) => updateFilter(e.target.value)}
                />
              </SearchWrapper>
            )}

            {filteredOptions.map((option, index) => {
              const classNames = ["--select-curtain-option"]
              activeIndex === index &&
                classNames.push("--select-curtain-option-active")
              value === option.value &&
                classNames.push("--select-curtain-option-selected")

              const className = classNames.join(" ")

              return (
                <CurtainOption
                  className={className}
                  key={option.value}
                  active={activeIndex === index}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </CurtainOption>
              )
            })}

            {!filteredOptions.length && (
              <CurtainOption
                className="--select-curtain-option --select-curtain-option-disabled"
                disabled
                active={false}
              >
                {noOptionsText}
              </CurtainOption>
            )}
          </div>
        </Curtain>
      )}
    </Wrapper>
  )
}

export const Select = React.forwardRef(BaseSelect) as <
  ValueType extends string | number,
  ExtraType = any,
>(
  props: SelectProps<ValueType, ExtraType> & {
    ref?: React.ForwardedRef<SelectRef>
  },
) => ReturnType<typeof BaseSelect>
